/**
 * DropdownRenderer Component
 * Displays dropdown select questions with optional "Other" text entry
 */

import React, { useState } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import type { Question } from '../../../types';
import QuestionHelpText from '../QuestionHelpText';

interface DropdownRendererProps {
  question: Question;
  selectedValues: Record<string, string>;
  onSelect: (values: Record<string, string>) => void;
}

function DropdownRenderer({
  question,
  selectedValues,
  onSelect,
}: DropdownRendererProps): JSX.Element {
  const dropdownSelects = question.dropdownSelects ?? question.metadata?.dropdownSelects ?? [];

  const handleSelectChange = (dropdownId: number, value: string): void => {
    const selectKey = `dropdown_${dropdownId}`;
    onSelect({
      ...selectedValues,
      [selectKey]: value,
    });
  };

  const handleOtherChange = (dropdownId: number, value: string): void => {
    const otherKey = `dropdown_${dropdownId}_other`;
    onSelect({
      ...selectedValues,
      [otherKey]: value,
    });
  };

  return (
    <Box className="cla-quiz-dropdowns" data-quiz-question-id={question.id} sx={{ mt: 2 }}>
      <QuestionHelpText description={question.metadata?.description} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {dropdownSelects.map((dropdown) => {
          const selectKey = `dropdown_${dropdown.id}`;
          const selectedValue = selectedValues[selectKey] || '';
          const otherKey = `dropdown_${dropdown.id}_other`;
          const otherValue = selectedValues[otherKey] || '';

          return (
            <Box className="cla-quiz-dropdown-group" data-quiz-dropdown-id={dropdown.id} key={dropdown.id}>
              <FormControl className="cla-quiz-dropdown" fullWidth required={dropdown.isRequired}>
                <InputLabel className="cla-quiz-field-label">{dropdown.label}</InputLabel>
                <Select
                  className="cla-quiz-field"
                  data-quiz-field-name={selectKey}
                  value={selectedValue}
                  label={dropdown.label}
                  onChange={(e) => handleSelectChange(dropdown.id, e.target.value)}
                >
                  {dropdown.options.map((option) => (
                    <MenuItem key={option.id} value={option.value}>
                      {option.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedValue && dropdown.options.find((o) => o.value === selectedValue)?.allowOtherValue && (
                <TextField
                  className="cla-quiz-field cla-quiz-dropdown-other"
                  inputProps={{ 'data-quiz-field-name': otherKey }}
                  label="Please specify..."
                  fullWidth
                  size="small"
                  value={otherValue}
                  onChange={(e) => handleOtherChange(dropdown.id, e.target.value)}
                  placeholder="Enter your answer"
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default DropdownRenderer;
